import { useEffect, useState } from 'react'
import { LocalStorageKey } from 'src/localStorage/enums/localStorageKey.enum'
import { useGetAiListeningProfilesQuery } from 'src/reactQuery/useGetAiListeningProfilesQuery'

export const useDebouncedValue = <T>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])

  return debounced
}

// The brand profile a viewer looked at last, so the sidebar's Brand Profile
// and Prompts links reopen it. A convenience only: storage can be blocked
// (private windows), and then the first profile is used.
export const rememberBrandProfile = (profileId: string) => {
  try {
    localStorage.setItem(LocalStorageKey.AI_LISTENING_LAST_PROFILE, profileId)
  } catch {
    // Storage unavailable — nothing to remember.
  }
}

const readRememberedBrandProfile = (): string | null => {
  try {
    return localStorage.getItem(LocalStorageKey.AI_LISTENING_LAST_PROFILE)
  } catch {
    return null
  }
}

// The user's brand profiles and the one to show: the requested id when it is
// theirs, else the last one viewed, else the first.
export const useResolvedBrandProfile = (requestedId?: string | null) => {
  const query = useGetAiListeningProfilesQuery()
  const profiles = query.data ?? []
  const remembered = readRememberedBrandProfile()

  const profile =
    profiles.find((item) => item._id === requestedId) ??
    profiles.find((item) => item._id === remembered) ??
    profiles[0]

  return {
    isError: query.isError,
    isLoading: query.isLoading,
    profile,
    profiles,
    refetch: query.refetch,
  }
}
